"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import type { NudgeSurface, ProfileFieldId } from "@/lib/profile/field-config";
import { getFieldCopy } from "@/lib/profile/field-labels";
import { recordNudgeEvent } from "@/server/actions/profile-nudge";
import { ProfileNudgeForm } from "./profile-nudge-form";

type Props = {
	field: ProfileFieldId;
	/**
	 * The surface label to record on nudge events (post_save_job, post_apply,
	 * post_job_view). Drives the cooldown logic in pickNextNudge — keep these
	 * distinct so a dismiss on one surface doesn't suppress the others.
	 */
	surface: Extract<
		NudgeSurface,
		"post_save_job" | "post_apply" | "post_job_view"
	>;
	/**
	 * Small caps eyebrow line above the field title. Different copy for each
	 * surface (Job saved → faster matches vs Application sent → boost profile).
	 */
	eyebrow: string;
	/** Auto-dismiss timer. Defaults to 8s for save, callers can pass 10s for apply. */
	autoDismissMs?: number;
	/** Profile dictionary for localized field copy + UI strings. */
	t?: Dictionary;
	onClose: () => void;
};

const DEFAULT_AUTO_DISMISS_MS = 8000;

/**
 * Reusable post-action sheet. Mobile bottom sheet / desktop bottom-right
 * toast. Used after both successful saves and apply clicks. Auto-dismisses
 * after `autoDismissMs` of no interaction (logs `shown` only — no
 * dismissed/snoozed unless the user explicitly chooses).
 */
export function PostActionSheet({
	field,
	surface,
	eyebrow,
	autoDismissMs = DEFAULT_AUTO_DISMISS_MS,
	t,
	onClose,
}: Props) {
	const copy = getFieldCopy(field, t);
	const closeLabel = t?.close ?? "Close";
	const notNowLabel = t?.notNow ?? "Not now";
	const router = useRouter();
	const [, startTransition] = useTransition();
	const [closing, setClosing] = useState(false);
	const sheetRef = useRef<HTMLDivElement>(null);
	const firstFocusRef = useRef<{ focus: () => void } | null>(null);
	const shownLogged = useRef(false);
	const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const interacted = useRef(false);

	const handleClose = useCallback(() => {
		setClosing(true);
		setTimeout(onClose, 180); // matches the slide-out duration
	}, [onClose]);

	// Log "shown" once per mount.
	useEffect(() => {
		if (shownLogged.current) return;
		shownLogged.current = true;
		startTransition(async () => {
			await recordNudgeEvent({ field, surface, action: "shown" });
		});
	}, [field, surface]);

	// Auto-dismiss after `autoDismissMs`, unless the user has interacted.
	useEffect(() => {
		dismissTimer.current = setTimeout(() => {
			if (!interacted.current) handleClose();
		}, autoDismissMs);
		return () => {
			if (dismissTimer.current) clearTimeout(dismissTimer.current);
		};
	}, [handleClose, autoDismissMs]);

	// Cancel auto-dismiss on first interaction inside the sheet.
	useEffect(() => {
		const node = sheetRef.current;
		if (!node) return;
		const cancel = () => {
			interacted.current = true;
			if (dismissTimer.current) {
				clearTimeout(dismissTimer.current);
				dismissTimer.current = null;
			}
		};
		node.addEventListener("focusin", cancel);
		node.addEventListener("pointerdown", cancel);
		return () => {
			node.removeEventListener("focusin", cancel);
			node.removeEventListener("pointerdown", cancel);
		};
	}, []);

	// Focus the first input on mount for power users.
	useEffect(() => {
		const t = setTimeout(() => firstFocusRef.current?.focus(), 50);
		return () => clearTimeout(t);
	}, []);

	const handleSnooze = () => {
		startTransition(async () => {
			await recordNudgeEvent({ field, surface, action: "snoozed" });
			router.refresh();
		});
		handleClose();
	};

	const handleSaved = () => {
		router.refresh();
		handleClose();
	};

	return (
		<>
			{/* Mobile scrim — desktop has none, the toast just floats. */}
			<button
				type="button"
				onClick={handleClose}
				aria-label={closeLabel}
				tabIndex={-1}
				className={`fixed inset-0 z-40 bg-background/40 backdrop-blur-[2px] transition-opacity duration-200 sm:hidden ${
					closing ? "opacity-0" : "opacity-100"
				}`}
			/>

			<div
				ref={sheetRef}
				role="dialog"
				aria-modal="false"
				aria-label={copy.formLabel}
				className={`fixed z-50 transition-transform duration-200 ease-out ${
					closing
						? "translate-y-4 opacity-0 sm:translate-y-0 sm:translate-x-4"
						: "translate-y-0 opacity-100 sm:translate-x-0"
				} bottom-0 left-0 right-0 rounded-t-2xl border-t border-border/60 bg-card p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-[0_-10px_40px_rgba(0,0,0,0.18)] sm:bottom-4 sm:left-auto sm:right-4 sm:w-[360px] sm:rounded-2xl sm:border sm:p-4`}
			>
				{/* Drag handle on mobile */}
				<div
					aria-hidden="true"
					className="mx-auto mb-3 h-1 w-10 rounded-full bg-border sm:hidden"
				/>

				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="text-xs font-semibold uppercase tracking-wider text-primary">
							{eyebrow}
						</p>
						<h3 className="mt-0.5 text-sm font-semibold text-foreground">
							{copy.formLabel}
						</h3>
						<p className="mt-1 text-xs text-muted-foreground">
							{copy.helpText}
						</p>
					</div>
					<button
						type="button"
						onClick={handleClose}
						aria-label={closeLabel}
						className="shrink-0 -mr-1 -mt-1 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						<svg
							width="14"
							height="14"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2.5"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<title>{closeLabel}</title>
							<path d="M18 6 6 18M6 6l12 12" />
						</svg>
					</button>
				</div>

				<div className="mt-3">
					<ProfileNudgeForm
						field={field}
						onSaved={handleSaved}
						firstFocusRef={firstFocusRef}
						t={t}
					/>
				</div>

				<div className="mt-3 flex items-center justify-between gap-3">
					<button
						type="button"
						onClick={handleSnooze}
						className="text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:underline"
					>
						{notNowLabel}
					</button>
				</div>
			</div>
		</>
	);
}
